import { createFileRoute, notFound, Link } from "@tanstack/react-router";

import { isPublicLocale, PUBLIC_LOCALES } from "@/components/legal-page";
import { SiteFooter } from "@/components/site-footer";
import { TopBar } from "@/components/top-bar";
import { SITE_URL } from "@/lib/site-url";

type Faq = { q: string; a: string };
type FaqContent = {
  metaTitle: string;
  metaDescription: string;
  heading: string;
  subheading: string;
  ctaTitle: string;
  ctaBody: string;
  ctaButton: string;
  notice?: string;
  groups: { title: string; items: Faq[] }[];
};

// Answers are written from the actual backend behaviour (cancellation window, refund
// rules, supported payment methods). If that behaviour changes, change these too.
const EN: FaqContent = {
  metaTitle: "FAQ — SmsEmpire | SMS verification, disposable email and accounts",
  metaDescription:
    "Answers about how SmsEmpire works: getting a number, the 20-minute cancellation window, refunds, payment methods, supported services and privacy.",
  heading: "Frequently asked questions",
  subheading: "How the service works, what it costs, and what happens when something goes wrong.",
  ctaTitle: "Still stuck?",
  ctaBody: "Send us a message and we'll get back to you. No account needed.",
  ctaButton: "Contact support",
  groups: [
    {
      title: "Getting started",
      items: [
        {
          q: "How does it work?",
          a: "Create an account, add balance, and pick the service you need to verify with. We allocate a real phone number, you use it on that platform, and the code appears in your activations page as soon as it arrives.",
        },
        {
          q: "Do I need to install anything?",
          a: "No. Everything runs in the browser, including receiving the code. There is no app to install and no SIM card involved.",
        },
        {
          q: "How long does a code take to arrive?",
          a: "Usually well under a minute, but the delivery time is set by the platform sending the message, not by us. If nothing arrives you can cancel and get your money back — see the cancellation window below.",
        },
        {
          q: "Can I choose the country of the number?",
          a: "Yes. By default we pick the best-quality country with stock, but you can open the country picker on any service and choose one yourself, with its live price and availability.",
        },
      ],
    },
    {
      title: "Money, prices and refunds",
      items: [
        {
          q: "How is the price decided?",
          a: "Prices are determined by our network and change with demand and stock, so the price you see is the price at that moment. We fetch the real price again when you press buy, so what you are charged is never higher than what was displayed.",
        },
        {
          q: "What is the cancellation window?",
          a: "You cannot cancel during the first 120 seconds — that minimum comes from our provider. From 120 seconds up to 20 minutes you can cancel and the full price returns to your balance immediately. After 20 minutes the activation expires and is not refunded, so cancel it yourself if no code arrives.",
        },
        {
          q: "What if the code arrives but the platform rejects the number?",
          a: "Once a code has been delivered the activation has been fulfilled and is not refundable. We sell access to a number and the delivery of the code sent to it — we cannot control whether a third-party platform accepts a given number.",
        },
        {
          q: "How can I pay?",
          a: "Card through Stripe, or cryptocurrency through NOWPayments — Bitcoin, Ethereum, USDT and many more. Minimum amounts are shown on the recharge page. Crypto transfers are irreversible, so send the exact amount on the network shown.",
        },
        {
          q: "Can I get unused balance back?",
          a: "Yes. Open a ticket from your account and we will return unused balance to your original payment method, less any fee our processor keeps. Balance already spent on activations cannot be returned.",
        },
      ],
    },
    {
      title: "Accounts and privacy",
      items: [
        {
          q: "What data do you keep about me?",
          a: "An email address, a username, your activation and top-up history, and your IP address for rate limiting. We do not ask for your real name or any identity document, and we never see your card number — Stripe handles that.",
        },
        {
          q: "Can I delete my account?",
          a: "Yes. Ask through a ticket and we will delete it. Records of paid transactions are retained where accounting law requires it, but they are restricted rather than used.",
        },
        {
          q: "Why do I have to verify my email?",
          a: "It is what stops one person creating unlimited accounts to farm referral credit, and it is the only way we can reach you about a payment or a refund.",
        },
        {
          q: "Is using a virtual number allowed?",
          a: "It depends on the platform you are registering with — many prohibit it in their own terms, and that is between you and them. Using our service to commit fraud, impersonate someone or evade a ban is prohibited by our terms and will get the account closed.",
        },
      ],
    },
  ],
};

const ES: FaqContent = {
  metaTitle: "Preguntas frecuentes — SmsEmpire | Verificación SMS, correo y cuentas",
  metaDescription:
    "Respuestas sobre cómo funciona SmsEmpire: conseguir un número, la ventana de cancelación de 20 minutos, reembolsos, métodos de pago, servicios y privacidad.",
  heading: "Preguntas frecuentes",
  subheading: "Cómo funciona el servicio, cuánto cuesta y qué pasa cuando algo sale mal.",
  ctaTitle: "¿Sigues atascado?",
  ctaBody: "Escríbenos y te respondemos. No hace falta tener cuenta.",
  ctaButton: "Contactar con soporte",
  groups: [
    {
      title: "Primeros pasos",
      items: [
        {
          q: "¿Cómo funciona?",
          a: "Creas una cuenta, cargas saldo y eliges el servicio en el que necesitas verificarte. Te asignamos un número de teléfono real, lo usas en esa plataforma y el código aparece en tu página de activaciones en cuanto llega.",
        },
        {
          q: "¿Tengo que instalar algo?",
          a: "No. Todo funciona en el navegador, incluida la recepción del código. No hay aplicación que instalar ni tarjeta SIM de por medio.",
        },
        {
          q: "¿Cuánto tarda en llegar el código?",
          a: "Normalmente bastante menos de un minuto, pero el tiempo de entrega lo marca la plataforma que envía el mensaje, no nosotros. Si no llega nada puedes cancelar y recuperar el dinero: mira la ventana de cancelación más abajo.",
        },
        {
          q: "¿Puedo elegir el país del número?",
          a: "Sí. Por defecto escogemos el país de mejor calidad con stock, pero puedes abrir el selector de países en cualquier servicio y elegirlo tú, con su precio y disponibilidad en tiempo real.",
        },
      ],
    },
    {
      title: "Dinero, precios y reembolsos",
      items: [
        {
          q: "¿Cómo se decide el precio?",
          a: "Los precios se determinan en nuestra red y cambian según la demanda y el stock, así que el precio que ves es el de ese momento. Volvemos a consultar el precio real al pulsar comprar, de modo que nunca se te cobra más de lo que se mostraba.",
        },
        {
          q: "¿Cuál es la ventana de cancelación?",
          a: "No puedes cancelar durante los primeros 120 segundos: ese mínimo lo impone la red. Desde los 120 segundos y hasta los 20 minutos puedes cancelar y el precio íntegro vuelve a tu saldo al instante. Pasados los 20 minutos la activación caduca y no se reembolsa, así que cancélala tú si no llega ningún código.",
        },
        {
          q: "¿Y si llega el código pero la plataforma rechaza el número?",
          a: "Una vez entregado el código, la activación se ha cumplido y no es reembolsable. Vendemos acceso a un número y la entrega del código que se le envía; no podemos controlar si una plataforma externa acepta un número concreto.",
        },
        {
          q: "¿Cómo puedo pagar?",
          a: "Con tarjeta a través de Stripe, o con criptomoneda a través de NOWPayments: Bitcoin, Ethereum, USDT y muchas más. Los importes mínimos se indican en la página de recarga. Las transferencias en cripto son irreversibles, así que envía el importe exacto por la red indicada.",
        },
        {
          q: "¿Puedo recuperar el saldo no gastado?",
          a: "Sí. Abre un ticket desde tu cuenta y devolvemos el saldo no consumido a tu medio de pago original, descontando la comisión que se quede la pasarela. El saldo ya gastado en activaciones no se puede devolver.",
        },
      ],
    },
    {
      title: "Cuenta y privacidad",
      items: [
        {
          q: "¿Qué datos guardáis sobre mí?",
          a: "Una dirección de correo, un nombre de usuario, tu historial de activaciones y recargas, y tu dirección IP para los límites de peticiones. No pedimos tu nombre real ni ningún documento de identidad, y nunca vemos el número de tu tarjeta: de eso se encarga Stripe.",
        },
        {
          q: "¿Puedo eliminar mi cuenta?",
          a: "Sí. Pídelo por ticket y la eliminamos. Los justificantes de operaciones con pago se conservan cuando la normativa contable lo exige, pero quedan limitados en lugar de utilizarse.",
        },
        {
          q: "¿Por qué tengo que verificar el correo?",
          a: "Es lo que impide que una sola persona cree cuentas ilimitadas para explotar el crédito de referidos, y es la única vía que tenemos para localizarte por un pago o un reembolso.",
        },
        {
          q: "¿Está permitido usar un número virtual?",
          a: "Depende de la plataforma en la que te registres: muchas lo prohíben en sus propias condiciones, y eso es un asunto entre esa plataforma y tú. Usar nuestro servicio para cometer fraude, suplantar a alguien o eludir un bloqueo está prohibido por nuestros términos y conlleva el cierre de la cuenta.",
        },
      ],
    },
  ],
};

const NOTICES: Record<string, string> = {
  fr: "Cette page n'est disponible qu'en anglais et en espagnol pour le moment.",
  de: "Diese Seite ist derzeit nur auf Englisch und Spanisch verfügbar.",
  it: "Questa pagina è al momento disponibile solo in inglese e spagnolo.",
  pt: "Esta página está disponível apenas em inglês e espanhol de momento.",
  ru: "Эта страница пока доступна только на английском и испанском языках.",
  zh: "本页面目前仅提供英文和西班牙文版本。",
};

function contentFor(lang: string): FaqContent {
  if (lang === "es") return ES;
  return NOTICES[lang] ? { ...EN, notice: NOTICES[lang] } : EN;
}

export const Route = createFileRoute("/$lang/faq")({
  beforeLoad: ({ params }) => {
    if (!isPublicLocale(params.lang)) throw notFound();
  },
  head: ({ params }) => {
    const lang = params.lang;
    const content = contentFor(lang);
    const canonical = `${SITE_URL}/${lang}/faq`;
    return {
      meta: [
        { title: content.metaTitle },
        { name: "description", content: content.metaDescription },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: content.metaTitle },
        { property: "og:description", content: content.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/faq` },
        ...PUBLIC_LOCALES.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/faq`,
        })),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: lang,
            mainEntity: content.groups.flatMap((group) =>
              group.items.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            ),
          }),
        },
      ],
    };
  },
  component: FaqPage,
});

function FaqPage() {
  const { lang } = Route.useParams();
  const content = contentFor(lang);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopBar />

      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12">
        <header>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">{content.heading}</h1>
          <p className="mt-3 text-muted-foreground">{content.subheading}</p>
          {content.notice && (
            <p className="mt-4 rounded-lg border border-border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
              {content.notice}
            </p>
          )}
        </header>

        <div className="mt-10 space-y-10">
          {content.groups.map((group) => (
            <section key={group.title}>
              <h2 className="text-lg font-semibold text-foreground">{group.title}</h2>
              <dl className="mt-4 space-y-5">
                {group.items.map((item) => (
                  <div key={item.q} className="rounded-xl border border-border bg-card/40 p-5">
                    <dt className="font-medium text-foreground">{item.q}</dt>
                    <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <section className="mt-12 rounded-2xl border border-border bg-card p-6 text-center shadow-elegant">
          <h2 className="text-xl font-semibold text-foreground">{content.ctaTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{content.ctaBody}</p>
          <Link
            to="/$lang/contact"
            params={{ lang }}
            className="mt-5 inline-flex items-center justify-center rounded-md bg-gradient-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-glow transition-opacity hover:opacity-90"
          >
            {content.ctaButton}
          </Link>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
